import { flatten } from 'underscore';
import React, { useEffect, useMemo } from 'react';
import {
  Menu,
  MenuList,
  MenuGroup,
  MenuGroupHeader,
  MenuTrigger,
  MenuButton,
  MenuItemRadio,
  MenuPopover,
  mergeClasses,
} from '@fluentui/react-components';
import { ChevronDown16Regular, ChevronRight20Regular } from '@fluentui/react-icons';
import { useScopeFilterMenuStyles } from '../styles';
import { IScopeFilterMenuProps } from '../types';

export const ScopeFilterMenu = (props: IScopeFilterMenuProps) => {
  const {
    menuName,
    menuItemsGroupList,
    onChange,
    inPopover,
    selectedValues,
  } = props;

  const defaultCheckedValues: Record<string, string[]> = useMemo(() => {
    const checkedValues = {};
    menuItemsGroupList.forEach((group) => {
      const filterKey = group.key;
      const defaultCheckedItems = group.menuItems.find((item) => item.defaultChecked);
      if (defaultCheckedItems) {
        checkedValues[filterKey] = [defaultCheckedItems.key];
      }
    });
    return checkedValues;
  }, [menuItemsGroupList]);

  const filterMenuKey = useMemo(() => menuItemsGroupList.map((group) => group.key).join('-'), [
    menuItemsGroupList,
  ]);

  const [selectedFilterValueMap, setSelectedFilterValueMap] = React.useState(selectedValues || defaultCheckedValues);

  useEffect(() => {
    if (selectedValues) {
      setSelectedFilterValueMap(selectedValues);
    }
  }, [selectedValues, defaultCheckedValues]);

  const updatedCheckedValues = (e, { name, checkedItems }) => {
    const newSelectedFilterValueMap = {
      ...selectedFilterValueMap,
      [name]: checkedItems,
    };

    if (onChange) {
      onChange(newSelectedFilterValueMap);
    }

    setSelectedFilterValueMap(newSelectedFilterValueMap);
  };

  const fillStyledIcon = (icon) => React.cloneElement(icon, {
    primaryFill: 'var(--colorCompoundBrandForeground1)',
  });

  const renderMenuItems = (groupKey, menuItems) => (
    menuItems.map((item) => (
      <MenuItemRadio
        key={item.key}
        icon={item.icon && fillStyledIcon(item.icon)}
        name={groupKey}
        value={item.key}
        id={item.id}
      >
        {item.name}
      </MenuItemRadio>
    ))
  );

  const renderMenuItemsGroups = () => (
    menuItemsGroupList.map((group) => (
      <MenuGroup key={group.key} id={`scope-filter-menu-group-${group.key}`}>
        <MenuGroupHeader>
          {group.name}
        </MenuGroupHeader>
        {renderMenuItems(group.key, group.menuItems)}
      </MenuGroup>
    ))
  );

  const getSelectedValues = () => {
    const checkedFiltersShortNames = Object.entries(selectedFilterValueMap).map(([checkedMenuGroupKey, checkedItems]) => {
      const menuItemsGroup = menuItemsGroupList.find((group) => group.key === checkedMenuGroupKey);
      if (menuItemsGroup) {
        return menuItemsGroup.menuItems.filter((item) => checkedItems.includes(item.key)).map((item) => item.description);
      }
      return [];
    });

    return flatten(checkedFiltersShortNames).join(', ');
  };

  const displayCheckedValues = () => {
    const selectedFilterValues = getSelectedValues();

    return (
      <span className={mergeClasses('filter-button-text', `scope-filter-menu-${filterMenuKey}`)}>
        <span className="filter-name">
          {menuName}
        </span>
        <span className="selected-filter-values">
          {selectedFilterValues}
        </span>
      </span>
    );
  };

  const styles = useScopeFilterMenuStyles();

  const renderButton = () => (
    <MenuButton
      appearance="subtle"
      menuIcon={
        inPopover
          ? <ChevronRight20Regular className="chevron-right" />
          : <ChevronDown16Regular className="chevron-down" />
      }
      className={mergeClasses(styles.menuButton)}
    >
      {displayCheckedValues()}
    </MenuButton>
  );

  return (
    <Menu
      checkedValues={selectedFilterValueMap}
      onCheckedValueChange={updatedCheckedValues}
    >
      <MenuTrigger disableButtonEnhancement>
        {renderButton()}
      </MenuTrigger>

      <MenuPopover>
        <MenuList>
          {renderMenuItemsGroups()}
        </MenuList>
      </MenuPopover>
    </Menu>
  );
};

ScopeFilterMenu.defaultProps = {
  inPopover: false,
  onChange: undefined,
  selectedValues: undefined,
};
